// IMPORTANT: import from the leaf paths, not from the `@/utils`
// barrel. The barrel transitively pulls `output/renderers/field-types`,
// which runs `getIntl().formatMessage` at module-load time and
// creates a "Object(...) is not a function" cycle when this file is
// imported by anything that lives upstream of umi's intl bootstrap
// (e.g. DVA models).
import { isTrueBoolean, parseJson } from '@/utils/infrastructure';
import { FIELD_TYPE_GROUPS, JSON_DATA_TYPES } from './constants';
import FieldHandlerFactory from './FieldHandlerFactory';

// Re-export utilities that were previously in the main file
export { parseFormulaFields, preloadAttachmentFiles } from './utils';
export * from './constants';

// A selector option is "real" only when it has a usable `id`. Backend
// payloads sometimes carry placeholder rows like `{id:null,text:null}`
// or `{id:'',text:''}` for fields that were set then cleared; if we
// let those through, Antd's <Select labelInValue> renders a closable
// tag and `UserTagRender` falls back to `Usuario ${value}` → the
// user sees a literal "Usuario null" chip. Treat empty strings and
// the literal "null" / "undefined" strings as missing too — a few
// adapters serialize `null` that way before it reaches the client.
const SELECTOR_PLACEHOLDER_VALUES = new Set([null, undefined, '', 'null', 'undefined']);
const isUsableSelectorId = (v) => !SELECTOR_PLACEHOLDER_VALUES.has(v);

/**
 * Reshape a raw record value into the structure FormBuilder expects
 * for the field's input component. Mirrors the pre-processing done
 * by `pages/Entities/.../AddNewItem.handleRecordData` so any edit
 * surface (inline `EditableCell`, popup drawer, …) feeds each
 * FormItem the exact same shape and the widgets render identically.
 *
 *   - Boolean fields → `isTrueBoolean(value)`.
 *   - Selector / remote-list fields → `parseJson(value).items`
 *     mapped to `{label, value}`; collapsed to a single object when
 *     the field is single-select. Items whose `id` is missing or a
 *     placeholder (`null`, `''`, `'null'`, `'undefined'`) are dropped
 *     before mapping; if nothing usable remains we return `undefined`
 *     so the FormItem stays empty instead of rendering a "Usuario
 *     null" chip from a degenerate `[{label:null,value:null}]`.
 *   - Attachment / Photo / Image / Signature → `parseJson(value)`
 *     (the FormBuilder upload widgets expect an object literal, not
 *     the raw JSON string stored in the row).
 *   - Everything else (text, number, date, time, url, …) passes
 *     through unchanged. The form widgets are responsible for their
 *     own empty-value handling — `AceDatePicker`, for example,
 *     already turns `null`/`""` into `undefined` internally.
 *
 * @param {Object} field    - Form field definition (`type`, `multiple` / `allowMultiple`).
 * @param {*}      rawValue - The value as stored on the record.
 * @returns {*} Shape-corrected value ready for `setFieldsValue`.
 */
export function prepareEditFieldValue(field, rawValue) {
  const type = field?.type;
  if (!type) return rawValue;

  if (FIELD_TYPE_GROUPS.BOOLEAN.includes(type)) {
    return isTrueBoolean(rawValue);
  }

  if (JSON_DATA_TYPES.includes(type)) {
    // Empty-ish raw values short-circuit straight to undefined so the
    // selector starts in its true "no value" state.
    if (rawValue == null || rawValue === '' || rawValue === 'null') return undefined;

    const parsed = parseJson(rawValue);
    const items = parsed?.items
      ?.filter((o) => o != null && isUsableSelectorId(o.id))
      ?.map((o) => ({ label: o.text ?? o.label, value: o.id }));

    if (!Array.isArray(items) || items.length === 0) return undefined;
    // `multiple` is the form-builder name; `allowMultiple` is the
    // entity-field name that survives in hydrated definitions. Either
    // one means the form expects an array.
    const isMultiple = !!(field.multiple || field.allowMultiple);
    if (items.length === 1 && !isMultiple) return items[0];
    return items;
  }

  if (FIELD_TYPE_GROUPS.ATTACHMENT.includes(type)) {
    return parseJson(rawValue);
  }

  return rawValue;
}

/**
 * Dynamic Field Configuration Mapping (Refactored)
 *
 * This function maps field configurations using a factory pattern with specialized handlers
 * for different field types. This approach follows EPC principles:
 *
 * - Extensible: Easy to add new field types by creating new handlers
 * - Performant: Optimized lookups and reduced cyclomatic complexity
 * - Consistent: Unified interfaces and standardized patterns
 *
 * @param {Object} baseConfig - Dynamic field base configuration
 * @param {Array<Object>} [configData] - Fields base configuration list
 * @param {Boolean} [dependency] - Enable field dependencies
 * @returns {Object} Field configuration object
 */
export function fieldConfigurationMapping(baseConfig, configData = [], dependency = true) {
  if (!baseConfig || !baseConfig.type) {
    throw new Error('Invalid baseConfig: type is required');
  }

  try {
    const handler = FieldHandlerFactory.getHandler(
      baseConfig.type,
      baseConfig,
      configData,
      dependency,
    );

    return handler.handle();
  } catch (error) {
    // Fallback to basic configuration to prevent complete failure
    return {
      type: 'text',
      knownType: baseConfig.type,
      name: baseConfig.internalName,
      label: baseConfig.displayText,
      value: baseConfig.value || baseConfig.defaultValue,
      rules: [{ required: dependency ? !!baseConfig.required : false }],
    };
  }
}
